import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { export2txt, exportCSV } from "../api/DataApi";
import { deleteWidget, saveDashboard, saveReport } from "../api/InsightApi";
import LayoutAction from "../actions/LayoutAction";
import { ChartTypes, ContentTypes, WidgetTypes } from "../constants/action-types";
import ScorecardWidget from "./widgets/ScorecardWidget";
import ChartPieWidget from "./widgets/ChartPieWidget";
import GoalWidget from "./widgets/GoalWidget";
import GridWidget from "./widgets/GridWidget";
import ChartLineWidget from "./widgets/ChartLineWidget";
import ChartBarWidget from "./widgets/ChartBarWidget";
import ConfirmDialog from "../dialog/ConfirmDialog";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import DashboardAction from "../actions/DashboardAction";
import { fromJS } from "immutable";
import ReportAction from "../actions/ReportAction";
import InputInlineWidget from "./components/InputInlineWidget";
import { ChartArea, Plus } from "lucide-react";
import { useCurrentSize } from "../CurrentSize";
import { AppContext } from "../Contexts";
import ListViewEmptyState from "../main/list-view/ListViewEmptyState";

const DashboardApp = () => {
  const currentSize = useCurrentSize();
  const { projectId, dashboardId }: any = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const appContext = useContext<any>(AppContext);

  const [size, setSize] = useState(currentSize);
  const [widgetIdDeletion, setWidgetIdDeletion] = useState<string>();

  const dashboards = useSelector((state: any) => state.dashboard.get("dashboards")) || fromJS([]);
  const dashboard = dashboards.find((item) => item.get("id") === dashboardId) || fromJS({});
  const widgets = dashboard.get("items") || fromJS([]);
  const widgetList = widgets.size > 0 ? widgets.toJS() : [];

  const deleteDashboardWidgetMutation = useMutation<any, Error, any>({
    mutationFn: async (widgetId) => {
      await deleteWidget(projectId, dashboardId, widgetId);
    },
    onSuccess: () => {
      dispatch(DashboardAction.getDashboards(projectId));
    },
  });

  const saveReportMutation = useMutation<any, Error, any>({
    mutationFn: async (report) => {
      await saveReport(projectId, report);
    },
    onSuccess: () => {
      dispatch(DashboardAction.getDashboards(projectId));
    },
  });

  const saveDashboardMutation = useMutation<any, Error, any>({
    mutationFn: async (dashboard) => {
      await saveDashboard(projectId, dashboard);
    },
    onSuccess: () => {
      dispatch(DashboardAction.getDashboards(projectId));
    },
  });

  useEffect(() => {
    if (dashboard) {
      document.title = dashboard.get("name") || "Dashboard";
    }
  }, [dashboard]);

  useEffect(() => {
    if (projectId) {
      dispatch(ReportAction.getReports(projectId));
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const minusHeight = appContext.appName == "chrome" ? 0 : 0;
    const minusWidth = appContext.appName == "chrome" ? 56 : 0;
    setSize({
      width: currentSize.width - minusWidth,
      height: currentSize.height - minusHeight,
    });
  }, [currentSize, appContext]);

  const handleAddNewReport = () => {
    navigate(
      `/workspaces/${projectId}/reports/dashboard/${dashboardId}/new?key=Reports&reportType=chart&reportChartType=line&reportObjectType=SALE&reportViewMode=false`
    );
  };

  const handleMoveUp = (widgetId) => {
    const newArray = [...widgetList];
    const index = widgetList.findIndex((widget) => widget.widgetId === widgetId);
    if (index > 0) {
      [newArray[index - 1], newArray[index]] = [newArray[index], newArray[index - 1]];
    }
    const updatedDashboard = {
      ...dashboard.toJS(),
      items: newArray,
    };
    saveDashboardMutation.mutate(updatedDashboard);
  };

  const handleMoveDown = (widgetId) => {
    const newArray = [...widgetList];
    const index = widgetList.findIndex((widget) => widget.widgetId === widgetId);
    if (index !== -1 && index < newArray.length - 1) {
      [newArray[index], newArray[index + 1]] = [newArray[index + 1], newArray[index]];
    }
    const updatedDashboard = {
      ...dashboard.toJS(),
      items: newArray,
    };
    saveDashboardMutation.mutate(updatedDashboard);
  };

  const handleExport = (report, filters) => {
    const gridType = report.data?.gridType || ContentTypes.SALE;
    const exportRequest = {
      filters,
      types: [gridType],
    };
    const filename = `${gridType}_${formatISO(new Date())}.csv`;
    exportCSV(report.projectId, exportRequest).then(({ data }) => {
      export2txt(data, filename);
      dispatch(
        LayoutAction.setNotification({
          title: t("CSV Export"),
          type: "success",
          message: t("Exported successfully"),
        })
      );
    });
  };

  const handleSetting = (report) => {
    navigate(
      `/workspaces/${projectId}/reports/dashboard/${dashboardId}/${report.id}?key=Reports&reportType=${
        report.type || ""
      }&reportChartType=${report.data?.chartType || ""}&reportObjectType=${
        report.data?.objectType || "SALE"
      }&reportViewMode=true`
    );
  };

  const handleDelete = (widgetId) => {
    setWidgetIdDeletion(widgetId);
  };

  const handleUpdateTitle = (report, newTitle) => {
    if (newTitle && newTitle !== report.data?.title) {
      const updatedWidget = {
        ...report,
        data: { ...report.data, title: newTitle },
      };
      saveReportMutation.mutate(updatedWidget);
    }
  };

  const handleUpdateSubtitle = (report, newSubtitle) => {
    if (newSubtitle && newSubtitle !== report.data?.subtitle) {
      const updatedWidget = {
        ...report,
        data: { ...report.data, subtitle: newSubtitle },
      };
      saveReportMutation.mutate(updatedWidget);
    }
  };

  const renderWidgets = useMemo(() => {
    return (
      <div className="grid grid-cols-12 gap-4 mb-4">
        {widgetList.map((widget) => {
          const report = widget.report;
          if (report?.type === WidgetTypes.goal) {
            return (
              <GoalWidget
                key={widget.widgetId}
                projectId={report.projectId}
                widget={report}
                className="col-span-12"
                onClickUp={() => handleMoveUp(widget.widgetId)}
                onClickDown={() => handleMoveDown(widget.widgetId)}
                onClickExport={(filters) => handleExport(report, filters)}
                onClickSetting={() => handleSetting(report)}
                onClickDelete={() => handleDelete(widget.widgetId)}
                onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
              />
            );
          } else if (report?.type === WidgetTypes.chart) {
            if (report?.data?.chartType === ChartTypes.pie) {
              return (
                <ChartPieWidget
                  key={widget.widgetId}
                  projectId={report.projectId}
                  widget={report}
                  className="col-span-12"
                  onClickUp={() => handleMoveUp(widget.widgetId)}
                  onClickDown={() => handleMoveDown(widget.widgetId)}
                  onClickExport={(filters) => handleExport(report, filters)}
                  onClickSetting={() => handleSetting(report)}
                  onClickDelete={() => handleDelete(widget.widgetId)}
                  onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
                  onUpdateSubtitle={(newSubtitle) => handleUpdateSubtitle(report, newSubtitle)}
                />
              );
            } else if (report?.data?.chartType === ChartTypes.line) {
              return (
                <ChartLineWidget
                  key={widget.widgetId}
                  projectId={report.projectId}
                  widget={report}
                  className="col-span-12"
                  onClickUp={() => handleMoveUp(widget.widgetId)}
                  onClickDown={() => handleMoveDown(widget.widgetId)}
                  onClickExport={(filters) => handleExport(report, filters)}
                  onClickSetting={() => handleSetting(report)}
                  onClickDelete={() => handleDelete(widget.widgetId)}
                  onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
                  onUpdateSubtitle={(newSubtitle) => handleUpdateSubtitle(report, newSubtitle)}
                />
              );
            } else {
              return (
                <ChartBarWidget
                  key={widget.widgetId}
                  projectId={report.projectId}
                  widget={report}
                  className="col-span-12"
                  onClickUp={() => handleMoveUp(widget.widgetId)}
                  onClickDown={() => handleMoveDown(widget.widgetId)}
                  onClickExport={(filters) => handleExport(report, filters)}
                  onClickSetting={() => handleSetting(report)}
                  onClickDelete={() => handleDelete(widget.widgetId)}
                  onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
                  onUpdateSubtitle={(newSubtitle) => handleUpdateSubtitle(report, newSubtitle)}
                />
              );
            }
          } else if (report?.type === WidgetTypes.grid) {
            return (
              <GridWidget
                key={widget.widgetId}
                projectId={report.projectId}
                widget={report}
                className="col-span-12"
                onClickUp={() => handleMoveUp(widget.widgetId)}
                onClickDown={() => handleMoveDown(widget.widgetId)}
                onClickExport={(filters) => handleExport(report, filters)}
                onClickSetting={() => handleSetting(report)}
                onClickDelete={() => handleDelete(widget.widgetId)}
                onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
                onUpdateSubtitle={(newSubtitle) => handleUpdateSubtitle(report, newSubtitle)}
              />
            );
          } else if (report?.type === WidgetTypes.scorecard) {
            return (
              <div key={widget.widgetId} className="col-span-12">
                <ScorecardWidget
                  projectId={report.projectId}
                  widget={report}
                  onClickUp={() => handleMoveUp(widget.widgetId)}
                  onClickDown={() => handleMoveDown(widget.widgetId)}
                  onClickExport={(filters) => handleExport(report, filters)}
                  onClickSetting={() => handleSetting(report)}
                  onClickDelete={() => handleDelete(widget.widgetId)}
                  onUpdateTitle={(newTitle) => handleUpdateTitle(report, newTitle)}
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  }, [widgetList]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDashboard = () => {
    if (widgetList.length === 0) {
      return (
        <div className={"pb-2 w-full h-full"}>
          <div className={"p-4"}>
            <ListViewEmptyState
              icon={ChartArea}
              title="Empty dashboard"
              description="No reports in this dashboard yet!"
              actionLabel="New report"
              onAction={handleAddNewReport}
            />
          </div>
        </div>
      );
    }
    return (
      <div className={"pb-2 w-full h-full"}>
        <div className={"p-4 space-y-4"}>
          {renderWidgets}
          <button
            onClick={handleAddNewReport}
            className="w-full flex items-center justify-center px-2.5 py-3 rounded-md border border-gray-200 hover:border-blue-600 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <ConfirmDialog
          visible={!!widgetIdDeletion}
          title={t("Delete widget")}
          description={t(
            "Are you sure you would like to delete this widget, this action is unable to undo?"
          )}
          alertIcon={<TriangleAlertIcon />}
          onSubmit={() => {
            deleteDashboardWidgetMutation.mutate(widgetIdDeletion);
            setWidgetIdDeletion(null);
          }}
          onClose={() => {
            setWidgetIdDeletion(null);
          }}
          dangerBtn={true}
        />
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="w-full pl-1.5 py-2 flex h-10 bg-white">
        <div className="flex flex-shrink-0 space-x-2">
          <div className="min-w-80 ml-3">
            <InputInlineWidget
              placeholder={t("Dashboard name")}
              value={dashboard.get("name")}
              className="text-sm sm:text-sm/6 md:text-base font-medium placeholder:text-gray-400 cursor-pointer"
              onFinish={(newValue) => {
                if (newValue && newValue !== dashboard.get("name")) {
                  const updatedDashboard = {
                    ...dashboard.toJS(),
                    name: newValue,
                  };
                  saveDashboardMutation.mutate(updatedDashboard);
                }
              }}
            />
          </div>
        </div>
        <div className="flex justify-end space-x-4 items-center pr-6 w-full">
          <button
            onClick={handleAddNewReport}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shrink-0"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("New report")}
          </button>
        </div>
      </div>
      <div style={{ maxWidth: size.width - 60 }}>{renderDashboard()}</div>
    </div>
  );
};

export default DashboardApp;
